/* =====================================================================
 * MessageInput — 메시지 입력 + 파일 첨부 (ChatGPT 스타일)
 * ===================================================================== */

import React, { useRef, useState, useCallback, useEffect, useMemo, Suspense, lazy } from 'react';
import styled, { css, keyframes, useTheme } from 'styled-components';
import { ArrowUp, Plus, X, Square, FileText, FileSpreadsheet, Image as ImageIcon, Loader, File, Maximize2, Minimize2, Upload, Cloud, Clipboard, Mail, Blocks, BookOpenText, BookOpen, MessageCircle, ChevronDown, Check, Sparkles, HardDrive, FolderOpen } from 'lucide-react';
import { v4 as uuid } from 'uuid';
import { useChatStore } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import type { FileAttachment, WorkspaceMode } from '@auw/workspace-utils';
import { WORKSPACE_MODES, WORKSPACE_ORDER, isWorkspaceMode } from '@auw/workspace-utils';
import { useFeatureFlags } from '@auw/feature-flags';
import { Tooltip } from '@auw/workspace-ui';
import { DropdownMenu, type DropdownMenuItem } from '@auw/workspace-ui';
import { Modal } from '@auw/workspace-ui';
import { canPreviewFile, getFileExtension } from '@auw/workspace-ui';
import {
  buildAttachmentCountLimitToast,
  buildAttachmentSizeLimitToast,
  buildAttachmentTypeLimitToast,
  validateAttachmentBatch,
} from '../attachmentConstraints';
import type { DriveProvider } from '../CloudDriveModal';
import type { CollaborationProvider, MailProvider, MessengerProvider } from '@auw/api-client';
import { useComposerTarget } from '@auw/workspace-features';
import { extractClipboardFiles } from '@auw/workspace-utils';
import { useReboModeStore, getReboModeOptions, type ReboMode } from '@auw/stores';
import { useSkillStore } from '@auw/stores';
import { useOfficialSkillStore } from '@auw/stores';
import { useChatFeature } from '../ChatModeContext';
import { resolveSpecializedAgents, resolveTenantModeOrder, useTenant } from '@auw/tenant';
import SkillModal from '../SkillModal';
import { Chip } from '@auw/workspace-ui';
import { getSpecializedStorageComponents } from '../../../lib/specializedStorageSlot';
import { WorkspaceFilesModal } from '../../storage/WorkspaceFilesModal';
import { resolveLocalized } from '@auw/workspace-utils';

const CloudDriveModal = lazy(() =>
  import('../CloudDriveModal').then((m) => ({ default: m.CloudDriveModal })),
);
const EmailModal = lazy(() =>
  import('../EmailModal').then((m) => ({ default: m.EmailModal })),
);
const CollaborationModal = lazy(() =>
  import('../CollaborationModal').then((m) => ({ default: m.CollaborationModal })),
);
const MessengerModal = lazy(() =>
  import('../MessengerModal').then((m) => ({ default: m.MessengerModal })),
);
import { emitAppEvent, onAppEvent } from '@auw/stores';
import { useTranslation } from 'react-i18next';

function cloneAttachmentsForSubmit(attachments: FileAttachment[]): FileAttachment[] {
  return attachments.map((attachment) => ({
    ...attachment,
    previewUrl:
      attachment.previewUrl?.startsWith('blob:') && attachment.file
        ? URL.createObjectURL(attachment.file)
        : attachment.previewUrl,
  }));
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Wrapper = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.sizes.maxMessageWidth};
  margin: 0 auto;
  padding: ${({ theme }) => theme.input.wrapperPad};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.inputMobile.wrapperPad};
  }
`;

const InputCard = styled.div<{ $focus: boolean }>`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgb(40 40 42 / calc(0.85 * var(--message-input-surface-opacity, 1)))'
      : 'rgb(255 255 255 / calc(0.85 * var(--message-input-surface-opacity, 1)))'};
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border-radius: ${({ theme }) => theme.sizes.borderRadiusLg};
  border: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 2px 12px rgba(0,0,0,0.25)'
      : '0 2px 12px rgba(0,0,0,0.05)'};
  transition: all 0.2s ease;
  ${({ $focus, theme }) =>
    $focus &&
    css`
      border-color: ${theme.mode === 'dark'
        ? 'rgba(255,255,255,0.14)'
        : 'rgba(0,0,0,0.1)'};
      box-shadow: ${theme.mode === 'dark'
        ? '0 4px 20px rgba(0,0,0,0.35), 0 0 0 0.5px rgba(255,255,255,0.08)'
        : '0 4px 20px rgba(0,0,0,0.08), 0 0 0 0.5px rgba(0,0,0,0.04)'};
    `}
`;

/* File previews row */
const Attachments = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.input.attachGap};
  padding: ${({ theme }) => theme.input.attachPad};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: ${({ theme }) => theme.inputMobile.attachGap};
    padding: ${({ theme }) => theme.inputMobile.attachPad};
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to   { opacity: 1; transform: scale(1); }
`;

/* ---- File card ---- */

const FileCard = styled.div<{ $previewable?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgb(255 255 255 / calc(0.04 * var(--message-input-surface-opacity, 1)))'
      : 'rgb(255 255 255 / calc(0.8 * var(--message-input-surface-opacity, 1)))'};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'};
  border-radius: 12px;
  padding: ${({ theme }) => theme.input.filePad};
  ${({ $previewable }) => $previewable && 'padding-right: 52px;'}
  min-width: 180px;
  max-width: 240px;
  animation: ${fadeIn} 0.2s ease;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 4px rgba(0,0,0,0.2)'
      : '0 1px 4px rgba(0,0,0,0.04)'};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 2px 8px rgba(0,0,0,0.3)'
        : '0 2px 8px rgba(0,0,0,0.06)'};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.inputMobile.filePad};
    ${({ $previewable }) => $previewable && 'padding-right: 48px;'}
    min-width: 160px;
    max-width: 200px;
  }
`;

const FileIconBadge = styled.div<{ $bg: string; $color: string }>`
  width: ${({ theme }) => theme.input.fileIconSize};
  height: ${({ theme }) => theme.input.fileIconSize};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  flex-shrink: 0;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ theme }) => theme.inputMobile.fileIconSize};
    height: ${({ theme }) => theme.inputMobile.fileIconSize};
  }

  svg { flex-shrink: 0; }
`;

const FileExtLabel = styled.span<{ $color: string }>`
  font-size: ${({ theme }) => theme.input.fileExtFontSize};
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${({ $color }) => $color};
  line-height: 1;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.inputMobile.fileExtFontSize};
  }
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FileName = styled.span`
  font-size: ${({ theme }) => theme.input.fileFontSize};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.inputMobile.fileFontSize};
  }
`;

const FileSize = styled.span`
  font-size: ${({ theme }) => theme.input.fileSizeFontSize};
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.2;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.inputMobile.fileSizeFontSize};
  }
`;

const ImageThumb = styled.img`
  width: ${({ theme }) => theme.input.fileIconSize};
  height: ${({ theme }) => theme.input.fileIconSize};
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ theme }) => theme.inputMobile.fileIconSize};
    height: ${({ theme }) => theme.inputMobile.fileIconSize};
  }
`;

/* ---- File card action buttons ---- */

const CardActions = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  gap: 1px;
`;

const CardActionBtn = styled.button`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  background: transparent;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* Input row */
const InputRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.input.rowPad};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.inputMobile.rowPad};
  }
`;

const ComposerInputWrap = styled.div`
  width: 100%;
  position: relative;
  padding: 8px 16px 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 6px 12px 0;
  }
`;

const EditorInput = styled.div`
  width: 100%;
  min-height: 24px;
  max-height: ${({ theme }) => theme.sizes.inputMaxHeight};
  overflow-y: hidden;
  padding: ${({ theme }) => theme.input.textPad};
  padding-left: 8px;
  padding-right: 8px;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.input.fontSize};
  line-height: ${({ theme }) => theme.input.lineHeight};
  white-space: pre-wrap;
  word-break: break-word;
  outline: none;
  /*
   * caret 가시성 — host 앱의 가장 핵심 UI 요소라 !important 로 보호.
   *
   * 사고 배경: contentEditable 의 caret-color 는 명시 안 되면 browser 가
   * text color 를 따라가는 게 보통이지만, 외부 stylesheet (예: vendor
   * docxEditor 의 editor-base.css 가 깔던 [contenteditable='true'] {
   * caret-color: #000 } 글로벌 룰 — 별도 패치로 .ep-root 안으로 좁힘) 와
   * specificity 가 동등해 cascade 순서로 외부 룰이 이기는 케이스가 발생
   * 했다. 다크 모드에서 캐럿이 검정으로 박혀 안 보이는 회귀가 보고됐다.
   *
   * 채팅 input 의 caret 은 사용자 시야와 입력 위치 정체성에 1:1 이라,
   * 어떤 vendor / 미래 leak 로부터도 보호되어야 한다. !important 가
   * 일반적으로 비호선이지만 이 자리에서는 정당화된다 — 같은 선택자에서
   * 이걸 의도적으로 덮어쓸 호출자가 없다.
   *
   * 자식 요소 (mention chip 등) 안에서 caret 이 떠 있을 때도 같은 색이
   * 되도록 명시 — caret-color 는 spec 상 inherit 되지만 일부 브라우저
   * 동작 차이를 막는 안전망.
   */
  caret-color: ${({ theme }) => theme.colors.textPrimary} !important;
  background: transparent;

  & * {
    caret-color: inherit;
  }

  &[data-empty='true'] {
    min-height: ${({ theme }) => theme.input.iconBtnSize};
  }

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 2px;
  }
  &[data-at-max='true']::-webkit-scrollbar-thumb {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'};
  }
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  &[data-at-max='true'] {
    scrollbar-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'} transparent;
  }

  [data-mention-chip='true'] {
    display: inline-flex;
    align-items: center;
    border-radius: 6px;
    padding: 0 6px;
    margin: 0 1px;
    font-size: 0.86em;
    line-height: 1.35;
    font-weight: 600;
    color: ${({ theme }) => (theme.mode === 'dark' ? '#7dd3fc' : '#1264a3')};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(56,189,248,0.18)' : 'rgba(18,100,163,0.14)'};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 22px;
    padding: ${({ theme }) => theme.inputMobile.textPad};
    padding-left: 6px;
    padding-right: 6px;
    font-size: ${({ theme }) => theme.inputMobile.fontSize};
    line-height: ${({ theme }) => theme.inputMobile.lineHeight};
  }
`;

const ComposerPlaceholder = styled.div`
  position: absolute;
  top: 12px;
  left: 16px;
  right: 16px;
  pointer-events: none;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: ${({ theme }) => theme.input.textPad};
  padding-left: 8px;
  padding-right: 8px;
  font-size: ${({ theme }) => theme.input.fontSize};
  line-height: ${({ theme }) => theme.input.lineHeight};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    top: 10px;
    left: 12px;
    right: 12px;
    padding: ${({ theme }) => theme.inputMobile.textPad};
    padding-left: 6px;
    padding-right: 6px;
    font-size: ${({ theme }) => theme.inputMobile.fontSize};
    line-height: ${({ theme }) => theme.inputMobile.lineHeight};
  }
`;

const MentionDropdown = styled.div`
  position: fixed;
  transform: translateY(-100%);
  z-index: 80;
  min-width: 220px;
  max-width: 320px;
  max-height: 220px;
  overflow-y: auto;
  border-radius: 12px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgb(40 40 42 / calc(0.92 * var(--message-input-surface-opacity, 1)))'
      : 'rgb(255 255 255 / calc(0.92 * var(--message-input-surface-opacity, 1)))'};
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.45), 0 1px 3px rgba(0,0,0,0.3)'
      : '0 8px 32px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.06)'};
  padding: 6px;
`;

const MentionSuggestionRow = styled.div<{ $focused: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${({ $focused, theme }) =>
    $focused
      ? (theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)')
      : 'transparent'};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  transition: background 0.15s ease;
`;

const MentionSuggestionType = styled.span`
  flex-shrink: 0;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 2px 6px;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.bgHover};
`;

const MentionSuggestionLabel = styled.span`
  min-width: 0;
  font-size: 0.82rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconBtn = styled.button<{ $primary?: boolean; $disabled?: boolean }>`
  width: ${({ theme }) => theme.input.iconBtnSize};
  height: ${({ theme }) => theme.input.iconBtnSize};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $primary, theme }) =>
    $primary
      ? css`
          background: ${theme.colors.textPrimary};
          color: ${theme.colors.bgPrimary};
          &:hover:not(:disabled) {
            opacity: 0.85;
          }
        `
      : css`
          color: ${theme.colors.textMuted};
          &:hover {
            background: ${theme.colors.bgHover};
            color: ${theme.colors.textPrimary};
          }
        `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.3;
      cursor: not-allowed;
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ theme }) => theme.inputMobile.iconBtnSize};
    height: ${({ theme }) => theme.inputMobile.iconBtnSize};
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const OuterWrapper = styled.div<{ $surfaceOpacity: number }>`
  position: relative;
  --message-input-surface-opacity: ${({ $surfaceOpacity }) => $surfaceOpacity};
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const UploadingBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px 0;
  font-size: ${({ theme }) => theme.input.uploadFontSize};
  color: ${({ theme }) => theme.colors.textMuted};

  svg {
    animation: ${spin} 1s linear infinite;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.inputMobile.uploadFontSize};
  }
`;

const SpinIcon = styled.span`
  display: flex;
  animation: ${spin} 1s linear infinite;
`;

/* ---- Pasted block card ---- */

const PastedCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 180px;
  max-width: 240px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgb(255 255 255 / calc(0.04 * var(--message-input-surface-opacity, 1)))'
      : 'rgb(255 255 255 / calc(0.8 * var(--message-input-surface-opacity, 1)))'};
  overflow: hidden;
  cursor: pointer;
  animation: ${fadeIn} 0.2s ease;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 4px rgba(0,0,0,0.2)'
      : '0 1px 4px rgba(0,0,0,0.04)'};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 2px 8px rgba(0,0,0,0.3)'
        : '0 2px 8px rgba(0,0,0,0.06)'};
  }
`;

const PastedPreview = styled.pre`
  margin: 0;
  padding: 10px 12px 28px;
  font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', Menlo, monospace;
  font-size: 0.7rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: pre;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  /* fade out bottom */
  -webkit-mask-image: linear-gradient(to bottom, black 50%, transparent 100%);
  mask-image: linear-gradient(to bottom, black 50%, transparent 100%);
`;

const PastedFooter = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 6px 4px 8px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgb(0 0 0 / calc(0.35 * var(--message-input-surface-opacity, 1)))'
      : 'rgb(255 255 255 / calc(0.85 * var(--message-input-surface-opacity, 1)))'};
  backdrop-filter: blur(4px);
`;

const PastedBadge = styled.span`
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#c8c8cc' : '#fff')};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.65)'};
  border-radius: 5px;
  padding: 2px 6px;
`;

const PastedCharCount = styled.span`
  font-size: 0.65rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const PastedRemoveBtn = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.bgHover};
  color: ${({ theme }) => theme.colors.textMuted};
  border: none;
  cursor: pointer;
  opacity: 0;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${PastedCard}:hover & {
    opacity: 1;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.bgTertiary};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* Pasted modal */
const PastedModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PastedModalTextarea = styled.textarea`
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: 14px 16px;
  font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', Menlo, monospace;
  font-size: 0.8rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  white-space: pre;
  word-break: break-word;
  resize: none;
  outline: none;
  height: 360px;
  max-height: 400px;
  overflow-y: auto;
  scrollbar-width: thin;
  &:focus {
    border-color: ${({ theme }) => theme.colors.borderInput};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.borderInput}33;
  }
`;

const PastedModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const PastedModalConfirmBtn = styled.button`
  padding: 9px 24px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  background: ${({ theme }) => theme.colors.textPrimary};
  color: ${({ theme }) => theme.colors.bgPrimary};
  border: none;
  cursor: pointer;
  transition: opacity ${({ theme }) => theme.transitions.fast};

  &:hover {
    opacity: 0.85;
  }
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

/* ---- Pasted block type ---- */

interface PastedBlock {
  id: string;
  content: string;
  forceInvokeAgent?: boolean;
}

/** 이 글자수 이상이면 카드로 접음 */
const PASTE_COLLAPSE_THRESHOLD = 500;

type MentionKind = 'agent' | 'member';
interface MentionSuggestion {
  id: string;
  display: string;
  kind: MentionKind;
}

interface MentionMenuState {
  open: boolean;
  x: number;
  y: number;
  query: string;
  activeIndex: number;
}

interface MentionTriggerContext {
  node: Text;
  startOffset: number;
  endOffset: number;
}

const AGENT_MENTION_ID = 'agent:askyour.ai';
const AGENT_MENTION_DISPLAY = 'askyour.ai';
const GROUP_AGENT_MENTION_RE = /(^|[\s([{"'])@askyour\.ai(?=$|[\s)\]}",.!?:;'"])/i;

function normalizeEditorText(text: string): string {
  return text.replace(/\u00A0/g, ' ').replace(/\u200B/g, '');
}

function extractPlainTextFromEditorNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return normalizeEditorText((node as Text).data);
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as HTMLElement;
  if (el.dataset.mentionChip === 'true') {
    const display = el.dataset.mentionDisplay || AGENT_MENTION_DISPLAY;
    return `@${display}`;
  }
  let out = '';
  node.childNodes.forEach((child) => {
    out += extractPlainTextFromEditorNode(child);
  });
  return out;
}

function extractPlainTextFromEditor(root: HTMLElement): string {
  let out = '';
  root.childNodes.forEach((node) => {
    out += extractPlainTextFromEditorNode(node);
  });
  return normalizeEditorText(out);
}

function editorHasAgentMention(root: HTMLElement): boolean {
  if (root.querySelector(`[data-mention-chip="true"][data-mention-id="${AGENT_MENTION_ID}"]`)) {
    return true;
  }
  return GROUP_AGENT_MENTION_RE.test(extractPlainTextFromEditor(root));
}

function removeMentionChipsFromEditor(root: HTMLElement): void {
  const chips = root.querySelectorAll<HTMLElement>('[data-mention-chip="true"]');
  chips.forEach((chip) => {
    const next = chip.nextSibling;
    chip.remove();
    if (next && next.nodeType === Node.TEXT_NODE) {
      const textNode = next as Text;
      textNode.data = textNode.data.replace(/^\u00A0/, '');
      if (!textNode.data) textNode.remove();
    }
  });
  root.normalize();
}

function placeCaretAtTextEnd(node: Text): void {
  const selection = window.getSelection();
  if (!selection) return;
  const nextRange = document.createRange();
  nextRange.setStart(node, node.data.length);
  nextRange.collapse(true);
  selection.removeAllRanges();
  selection.addRange(nextRange);
}

function insertPlainTextAtSelection(root: HTMLElement, text: string): void {
  const selection = window.getSelection();
  if (!selection) return;

  const normalized = normalizeEditorText(text);
  let range: Range;

  if (
    selection.rangeCount > 0
    && root.contains(selection.getRangeAt(0).startContainer)
    && root.contains(selection.getRangeAt(0).endContainer)
  ) {
    range = selection.getRangeAt(0);
  } else {
    range = document.createRange();
    range.selectNodeContents(root);
    range.collapse(false);
  }

  range.deleteContents();
  const textNode = document.createTextNode(normalized);
  range.insertNode(textNode);

  const nextRange = document.createRange();
  nextRange.setStart(textNode, textNode.data.length);
  nextRange.collapse(true);
  selection.removeAllRanges();
  selection.addRange(nextRange);
}

function getCaretRect(range: Range): DOMRect {
  const rect = range.getBoundingClientRect();
  if (rect.width > 0 || rect.height > 0) {
    return rect;
  }
  const marker = document.createElement('span');
  marker.textContent = '\u200B';
  range.insertNode(marker);
  const markerRect = marker.getBoundingClientRect();
  const markerParent = marker.parentNode;
  if (markerParent) markerParent.removeChild(marker);
  range.collapse(false);
  const sel = window.getSelection();
  if (sel) {
    sel.removeAllRanges();
    sel.addRange(range);
  }
  return markerRect;
}

/* ---- File type helpers ---- */

interface FileTypeInfo {
  icon: React.ReactNode;
  color: string;
  bg: string;
  ext: string;
}

function getFileTypeInfo(name: string, type: string): FileTypeInfo {
  const ext = getFileExtension(name);

  if (type.startsWith('image/'))
    return { icon: <ImageIcon size={18} />, color: '#8B5CF6', bg: 'rgba(139,92,246,0.1)', ext: ext.toUpperCase() };

  switch (ext) {
    case 'pdf':
      return { icon: <FileText size={18} />, color: '#EF4444', bg: 'rgba(239,68,68,0.1)', ext: 'PDF' };
    case 'xls': case 'xlsx':
      return { icon: <FileSpreadsheet size={18} />, color: '#22C55E', bg: 'rgba(34,197,94,0.1)', ext: ext.toUpperCase() };
    case 'csv':
      return { icon: <FileSpreadsheet size={18} />, color: '#22C55E', bg: 'rgba(34,197,94,0.1)', ext: 'CSV' };
    case 'doc': case 'docx':
      return { icon: <FileText size={18} />, color: '#3B82F6', bg: 'rgba(59,130,246,0.1)', ext: ext.toUpperCase() };
    case 'ppt': case 'pptx':
      return { icon: <FileText size={18} />, color: '#F97316', bg: 'rgba(249,115,22,0.1)', ext: ext.toUpperCase() };
    case 'txt': case 'md':
      return { icon: <FileText size={18} />, color: '#6B7280', bg: 'rgba(107,114,128,0.1)', ext: ext.toUpperCase() };
    default:
      return { icon: <File size={18} />, color: '#6B7280', bg: 'rgba(107,114,128,0.1)', ext: ext.toUpperCase() || 'FILE' };
  }
}

function formatFileSize(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface MessageInputProps {
  modeOverride?: WorkspaceMode;
  placeholderOverride?: string;
  surfaceOpacity?: number;
  /**
   * 지정 시 "+" 드롭다운을 여기 나열된 top-level id 만 노출하도록 제한한다.
   * (예: 견적 상세 페이지는 ['skill-load', 'workspace-files'] 만 노출)
   * 미지정이면 테넌트 기능 게이트에 따른 전체 항목을 노출한다.
   */
  plusMenuItemIds?: readonly string[];
  beforeSubmit?: (payload: {
    text: string;
    files: FileAttachment[];
    pastedContent: string;
  }) => boolean | Promise<boolean>;
  onSubmitOverride?: (payload: {
    text: string;
    files: FileAttachment[];
    pastedContent: string;
  }) => void | Promise<void>;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  modeOverride,
  placeholderOverride,
  surfaceOpacity = 1,
  beforeSubmit,
  onSubmitOverride,
  plusMenuItemIds,
}) => {
  const { t, i18n } = useTranslation('common');
  const theme = useTheme();
  const reboMode = useReboModeStore((s) => s.mode);
  const setReboMode = useReboModeStore((s) => s.setMode);
  const reboModeOptions = useMemo(() => getReboModeOptions(), [i18n.language]);
  const [reboMenuOpen, setReboMenuOpen] = useState(false);
  const reboTriggerRef = useRef<HTMLButtonElement>(null);
  const reboMenuRef = useRef<HTMLDivElement>(null);

  const skillModalOpen = useSkillStore((s) => s.isModalOpen);
  const skillModalInitialTab = useSkillStore((s) => s.modalInitialTab);
  const openSkillModal = useSkillStore((s) => s.openModal);
  const closeSkillModal = useSkillStore((s) => s.closeModal);
  const activeSkillIds = useSkillStore((s) => s.activeSkillIds);
  const skillsList = useSkillStore((s) => s.skills);
  const toggleSkill = useSkillStore((s) => s.toggleSkill);
  const officialSkills = useOfficialSkillStore((s) => s.skills);

  useEffect(() => {
    if (!reboMenuOpen) return;
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        reboTriggerRef.current?.contains(target) ||
        reboMenuRef.current?.contains(target)
      ) return;
      setReboMenuOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [reboMenuOpen]);

  const sendMessage = useChatStore((s) => s.sendMessage);
  const stopStreaming = useChatStore((s) => s.stopStreaming);
  const setPreviewFile = useChatStore((s) => s.setPreviewFile);
  const previewFile = useChatStore((s) => s.previewFile);
  const narrowPreviewView = useChatStore((s) => s.narrowPreviewView);
  const isNarrowLayout = useChatStore((s) => s.isNarrowLayout);
  const setNarrowPreviewView = useChatStore((s) => s.setNarrowPreviewView);
  const addToast = useChatStore((s) => s.addToast);
  const currentUser = useUserStore((s) => s.user);
  const {
    workspaceMode: resolvedWorkspaceMode,
    conversation: currentConversation,
    runtimeConversationId,
    isStreaming,
    isUploading,
  } = useComposerTarget(modeOverride);
  const isUploadingConversation = isUploading;
  /* In widget / embedded surfaces the composer target is driven by the
   * store's active conversation rather than the router route, so
   * `useComposerTarget` (route-based) returns `null` there. Fall back to the
   * active conversation so group-chat mentions (@model / @teammate) keep
   * working when resuming an existing team group chat from the widget. */
  const storeActiveConversation = useChatStore((s) => s.activeConversation());
  const groupChatConversation = currentConversation ?? storeActiveConversation;
  const isGroupChat = !!groupChatConversation?.sharedGroup;
  const currentUserId = currentUser?.userId || currentUser?.email || '';

  const [editorPlainText, setEditorPlainText] = useState('');
  const [editorIsEmpty, setEditorIsEmpty] = useState(true);
  const [editorAtMax, setEditorAtMax] = useState(false);
  const [forceInvokeAgentOnce, setForceInvokeAgentOnce] = useState(false);
  const [files, setFiles] = useState<FileAttachment[]>([]);
  const [pastedBlocks, setPastedBlocks] = useState<PastedBlock[]>([]);
  const [viewingBlock, setViewingBlock] = useState<PastedBlock | null>(null);
  const [focused, setFocused] = useState(false);
  const [mentionMenu, setMentionMenu] = useState<MentionMenuState>({
    open: false,
    x: 0,
    y: 0,
    query: '',
    activeIndex: 0,
  });
  const [plusMenu, setPlusMenu] = useState<{ open: boolean; x: number; y: number }>({ open: false, x: 0, y: 0 });
  const [workspaceFilesModal, setWorkspaceFilesModal] = useState<{
    open: boolean;
    /** 전문 에이전트(CAD/견적) 저장소일 때만 사용. */
    agentId?: string;
    /** 코어 워크스페이스 모드(통합/라이브/문서) 저장소일 때만 사용. */
    mode?: WorkspaceMode;
    agentLabel: string;
  }>({ open: false, agentId: '', agentLabel: '' });
  const [cloudDriveModal, setCloudDriveModal] = useState<{ open: boolean; initialTab: DriveProvider }>({ open: false, initialTab: 'google_drive' });
  const [emailModal, setEmailModal] = useState<{ open: boolean; initialTab: MailProvider }>({ open: false, initialTab: 'gmail' });
  const [collaborationModal, setCollaborationModal] = useState<{ open: boolean; initialTab: CollaborationProvider }>({ open: false, initialTab: 'confluence' });
  const [messengerModal, setMessengerModal] = useState<{ open: boolean; initialTab: MessengerProvider }>({ open: false, initialTab: 'slack' });
  const [modalCenterPadding, setModalCenterPadding] = useState({ left: 0, right: 0 });
  const outerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const mentionTriggerRef = useRef<MentionTriggerContext | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const filesRef = useRef<FileAttachment[]>([]);

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const mentionSuggestions = useMemo<MentionSuggestion[]>(() => {
    if (!isGroupChat || !groupChatConversation?.sharedGroup) return [];
    const memberSuggestions: MentionSuggestion[] = groupChatConversation.sharedGroup.members
      .filter((m) => m.user_id !== currentUserId)
      .map((m) => {
        const display = (m.name?.trim() || m.email?.split('@')[0] || t('chat.teamMember')).trim();
        return {
          id: `member:${m.user_id}`,
          display,
          kind: 'member',
        };
      });
    return [
      { id: AGENT_MENTION_ID, display: AGENT_MENTION_DISPLAY, kind: 'agent' },
      ...memberSuggestions,
    ];
  }, [groupChatConversation, currentUserId, isGroupChat]);

  const composerPlaceholder = placeholderOverride ?? (isGroupChat
    ? t('chat.mentionPlaceholder')
    : t('chat.messagePlaceholder'));

  const filteredMentionSuggestions = useMemo(() => {
    const query = mentionMenu.query.trim().toLowerCase();
    if (!query) return mentionSuggestions;
    return mentionSuggestions.filter((item) =>
      item.display.toLowerCase().includes(query),
    );
  }, [mentionMenu.query, mentionSuggestions]);

  const resetComposerForDraft = useCallback((focusInput = false) => {
    const editor = editorRef.current;
    if (editor) editor.innerHTML = '';
    setEditorPlainText('');
    setEditorIsEmpty(true);
    setEditorAtMax(false);
    setForceInvokeAgentOnce(false);
    mentionTriggerRef.current = null;
    setMentionMenu((prev) => ({ ...prev, open: false, query: '', activeIndex: 0 }));
    setFiles((prev) => {
      prev.forEach((f) => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
      return [];
    });
    setPastedBlocks([]);
    setPreviewFile(null);
    if (focusInput) {
      setTimeout(() => editorRef.current?.focus(), 0);
    }
  }, [setPreviewFile]);

  /* 워크스페이스 모드 변경 시 첨부 파일 초기화 */
  const prevWorkspaceMode = useRef(resolvedWorkspaceMode);
  useEffect(() => {
    if (prevWorkspaceMode.current !== resolvedWorkspaceMode) {
      resetComposerForDraft();
    }
    prevWorkspaceMode.current = resolvedWorkspaceMode;
  }, [resolvedWorkspaceMode, resetComposerForDraft]);

  useEffect(() => {
    const host = outerRef.current;
    if (!host) return;

    const layoutMain = host.closest('main');
    if (!(layoutMain instanceof HTMLElement)) {
      setModalCenterPadding((prev) => (
        prev.left === 0 && prev.right === 0 ? prev : { left: 0, right: 0 }
      ));
      return;
    }

    let rafId: number | null = null;
    const update = () => {
      rafId = null;
      const rect = layoutMain.getBoundingClientRect();
      const next = {
        left: Math.max(0, Math.round(rect.left)),
        right: Math.max(0, Math.round(window.innerWidth - rect.right)),
      };
      setModalCenterPadding((prev) => (
        prev.left === next.left && prev.right === next.right ? prev : next
      ));
    };
    const schedule = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(update);
    };

    schedule();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    observer?.observe(layoutMain);
    window.addEventListener('resize', schedule);
    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [resolvedWorkspaceMode]);

  /* 현재 입력창이 연결된 대화 전환 시 첨부 파일 초기화 */
  const prevConversationId = useRef(runtimeConversationId);
  const prevConversationRef = useRef<typeof currentConversation>(currentConversation);
  useEffect(() => {
    const prevId = prevConversationId.current;
    if (prevId !== runtimeConversationId) {
      const prevConv = prevConversationRef.current;
      const keepComposer =
        runtimeConversationId === null
        && !!prevConv
        && !prevConv.messages.some((msg) => msg.role === 'user' || msg.role === 'assistant');
      if (!keepComposer) {
        resetComposerForDraft();
      }
    }
    prevConversationId.current = runtimeConversationId;
    prevConversationRef.current = currentConversation;
  }, [runtimeConversationId, currentConversation, resetComposerForDraft]);

  /* P11-5e: hoisted up so `updateMentionMenuFromSelection` (defined a few
   * lines below) can reference it without a TDZ violation. The full
   * feature-flag bundle is still consumed below for plus-menu logic. */
  // PR-FE-1: was `useFeatureFlags().isEnabled('mode.agent.groupChat')` —
  // ``useChatFeature`` resolves the namespace via the active
  // :data:`ChatModeContext` (defaults to ``agent`` so legacy callers
  // are unaffected; doc-agent renders this ChatArea with mode prop
  // and gets ``mode.doc-agent.groupChat`` automatically).
  const groupChatMentionEnabled = useChatFeature('groupChat');

  const closeMentionMenu = useCallback(() => {
    mentionTriggerRef.current = null;
    setMentionMenu((prev) =>
      prev.open
        ? { ...prev, open: false, query: '', activeIndex: 0 }
        : prev,
    );
  }, []);

  const syncEditorState = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) {
      setEditorPlainText('');
      setEditorIsEmpty(true);
      setEditorAtMax(false);
      return;
    }
    const plain = extractPlainTextFromEditor(editor);
    setEditorPlainText(plain);
    setEditorIsEmpty(plain.trim().length === 0);
    const maxH = parseInt(getComputedStyle(editor).maxHeight, 10) || 200;
    const atMax = editor.scrollHeight > maxH + 1;
    setEditorAtMax(atMax);
    editor.style.overflowY = atMax ? 'auto' : 'hidden';
  }, []);

  const updateMentionMenuFromSelection = useCallback(() => {
    /* P11-5e: when the tenant has disabled `mode.agent.groupChat` the
     * mention picker is suppressed even if the conversation has a
     * shared group attached (legacy data). */
    if (!groupChatMentionEnabled || !isGroupChat || mentionSuggestions.length === 0) {
      closeMentionMenu();
      return;
    }
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || !selection.rangeCount || !selection.isCollapsed) {
      closeMentionMenu();
      return;
    }
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.startContainer) || range.startContainer.nodeType !== Node.TEXT_NODE) {
      closeMentionMenu();
      return;
    }
    const node = range.startContainer as Text;
    const endOffset = range.startOffset;
    const before = node.data.slice(0, endOffset);
    const match = /(?:^|[\s([{"'])@([^\s@]*)$/.exec(before);
    if (!match) {
      closeMentionMenu();
      return;
    }
    const query = match[1] ?? '';
    const startOffset = endOffset - query.length - 1;
    if (startOffset < 0) {
      closeMentionMenu();
      return;
    }
    const filtered = mentionSuggestions.filter((item) =>
      item.display.toLowerCase().includes(query.toLowerCase()),
    );
    if (filtered.length === 0) {
      closeMentionMenu();
      return;
    }
    mentionTriggerRef.current = { node, startOffset, endOffset };
    const rect = getCaretRect(range.cloneRange());
    const x = Math.max(8, Math.min(rect.left, window.innerWidth - 340));
    const y = Math.max(8, rect.top - 8);
    setMentionMenu((prev) => ({
      ...prev,
      open: true,
      query,
      x,
      y,
      activeIndex: Math.min(prev.activeIndex, filtered.length - 1),
    }));
  }, [closeMentionMenu, groupChatMentionEnabled, isGroupChat, mentionSuggestions]);

  const insertMentionChip = useCallback((mention: MentionSuggestion) => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    const trigger = mentionTriggerRef.current;
    if (!editor || !selection || !selection.rangeCount || !trigger || !trigger.node.isConnected) return;
    const currentRange = selection.getRangeAt(0);
    const replaceRange = document.createRange();
    replaceRange.setStart(trigger.node, trigger.startOffset);
    if (currentRange.startContainer === trigger.node && currentRange.startOffset >= trigger.startOffset) {
      replaceRange.setEnd(trigger.node, currentRange.startOffset);
    } else {
      replaceRange.setEnd(trigger.node, trigger.endOffset);
    }
    replaceRange.deleteContents();

    const chip = document.createElement('span');
    chip.dataset.mentionChip = 'true';
    chip.dataset.mentionId = mention.id;
    chip.dataset.mentionDisplay = mention.display;
    chip.dataset.mentionKind = mention.kind;
    chip.contentEditable = 'false';
    chip.textContent = `@${mention.display}`;

    const spacer = document.createTextNode('\u00A0');
    replaceRange.insertNode(spacer);
    replaceRange.insertNode(chip);

    editor.normalize();
    placeCaretAtTextEnd(spacer);
    syncEditorState();
    closeMentionMenu();
    editor.focus();
  }, [closeMentionMenu, syncEditorState]);

  useEffect(() => {
    if (!mentionMenu.open) return;
    if (filteredMentionSuggestions.length === 0) {
      closeMentionMenu();
      return;
    }
    setMentionMenu((prev) => (
      prev.activeIndex >= filteredMentionSuggestions.length
        ? { ...prev, activeIndex: filteredMentionSuggestions.length - 1 }
        : prev
    ));
  }, [closeMentionMenu, filteredMentionSuggestions.length, mentionMenu.open]);

  const handleEditorInput = useCallback(() => {
    syncEditorState();
    updateMentionMenuFromSelection();
  }, [syncEditorState, updateMentionMenuFromSelection]);

  const removePastedBlock = (id: string) => {
    setPastedBlocks((prev) => {
      const next = prev.filter((b) => b.id !== id);
      setForceInvokeAgentOnce(next.some((b) => !!b.forceInvokeAgent));
      return next;
    });
  };

  const notifyAttachmentConstraintViolations = useCallback((
    rejectedBySize: readonly { name?: string; size?: number | null }[],
    rejectedByCount: readonly { name?: string; size?: number | null }[],
    rejectedByType?: readonly { name?: string; size?: number | null }[],
  ) => {
    if (rejectedByType && rejectedByType.length > 0) {
      const toast = buildAttachmentTypeLimitToast(rejectedByType);
      addToast({
        type: 'warning',
        title: toast.title,
        description: toast.description,
      });
    }

    if (rejectedBySize.length > 0) {
      const toast = buildAttachmentSizeLimitToast(rejectedBySize);
      addToast({
        type: 'warning',
        title: toast.title,
        description: toast.description,
      });
    }

    if (rejectedByCount.length > 0) {
      const toast = buildAttachmentCountLimitToast(rejectedByCount.length);
      addToast({
        type: 'warning',
        title: toast.title,
        description: toast.description,
      });
    }
  }, [addToast]);

  const appendAttachmentEntries = useCallback((incoming: FileAttachment[]) => {
    if (incoming.length === 0) return;

    const { accepted, rejectedBySize, rejectedByCount, rejectedByType } = validateAttachmentBatch(
      incoming,
      filesRef.current.length,
    );

    for (const rejected of [...rejectedBySize, ...rejectedByCount, ...rejectedByType]) {
      if (rejected.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(rejected.previewUrl);
      }
    }

    notifyAttachmentConstraintViolations(rejectedBySize, rejectedByCount, rejectedByType);

    if (accepted.length === 0) return;
    filesRef.current = [...filesRef.current, ...accepted];
    setFiles((prev) => [...prev, ...accepted]);
  }, [notifyAttachmentConstraintViolations]);

  const addFiles = useCallback((incoming: FileList | File[] | null) => {
    if (!incoming) return;
    const fileList = Array.isArray(incoming) ? incoming : Array.from(incoming);
    const { accepted, rejectedBySize, rejectedByCount, rejectedByType } = validateAttachmentBatch(
      fileList,
      filesRef.current.length,
    );

    notifyAttachmentConstraintViolations(rejectedBySize, rejectedByCount, rejectedByType);

    if (accepted.length === 0) return;

    const arr: FileAttachment[] = accepted.map((f) => ({
      id: uuid(),
      name: f.name,
      type: f.type,
      size: f.size,
      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
      file: f,
    }));
    filesRef.current = [...filesRef.current, ...arr];
    setFiles((prev) => [...prev, ...arr]);
  }, [notifyAttachmentConstraintViolations]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const clipboardFiles = extractClipboardFiles(e.clipboardData);
    if (clipboardFiles.length > 0) {
      e.preventDefault();
      addFiles(clipboardFiles);
      closeMentionMenu();
      return;
    }

    const pasted = e.clipboardData.getData('text/plain') || e.clipboardData.getData('text');
    if (!pasted) return;

    if (pasted.length >= PASTE_COLLAPSE_THRESHOLD) {
      e.preventDefault();
      const editor = editorRef.current;
      let shouldForceInvoke = false;
      if (editor) {
        shouldForceInvoke = editorHasAgentMention(editor);
        if (shouldForceInvoke) {
          setForceInvokeAgentOnce(true);
        }
        removeMentionChipsFromEditor(editor);
        syncEditorState();
      }
      setPastedBlocks((prev) => [
        ...prev,
        { id: uuid(), content: pasted, forceInvokeAgent: shouldForceInvoke },
      ]);
      closeMentionMenu();
      return;
    }

    e.preventDefault();
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    insertPlainTextAtSelection(editor, pasted);
    syncEditorState();
    updateMentionMenuFromSelection();
  }, [addFiles, closeMentionMenu, syncEditorState, updateMentionMenuFromSelection]);

  useEffect(() => {
    return onAppEvent('chat:addFiles', (detail) => {
      if (detail?.files && detail.files.length > 0) {
        addFiles(detail.files);
      }
    });
  }, [addFiles]);

  useEffect(() => {
    return onAppEvent('chat:addMailAttachments', (detail) => {
      if (detail?.files && detail.files.length > 0) {
        appendAttachmentEntries(detail.files);
      }
    });
  }, [appendAttachmentEntries]);

  const removeFile = (id: string) => {
    if (previewFile?.id === id) setPreviewFile(null);
    setFiles((prev) => {
      const f = prev.find((a) => a.id === id);
      if (f?.previewUrl) URL.revokeObjectURL(f.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  };

  const resetComposer = () => {
    const editor = editorRef.current;
    if (editor) editor.innerHTML = '';
    setEditorPlainText('');
    setEditorIsEmpty(true);
    setEditorAtMax(false);
    setForceInvokeAgentOnce(false);
    closeMentionMenu();
    files.forEach((f) => {
      if (f.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(f.previewUrl);
      }
    });
    setFiles([]);
    setPastedBlocks([]);
    /* 모바일에서만 미리보기 닫기 — 데스크톱에서는 유지 */
    if (window.matchMedia(`(max-width: ${theme.breakpoints.mobile})`).matches) {
      setPreviewFile(null);
    }
    setTimeout(() => editorRef.current?.focus(), 0);
  };

  const handleSubmit = async () => {
    const trimmed = editorPlainText.trim();
    if (!trimmed && files.length === 0 && pastedBlocks.length === 0) return;

    const pastedContent = pastedBlocks.map((b) => b.content).join('\n\n');
    const fullText = [trimmed, pastedContent].filter(Boolean).join('\n\n');

    // 첨부 파일만 있고 에이전트에 줄 질문 텍스트가 없으면(서버도 query min_length=1 로
    // 거부해 "Agent server error" 가 됨) 전송하지 않고 toast 로 질문 작성을 안내한다.
    // 1474 가드를 통과한 뒤 fullText 가 비었다는 것은 파일만 첨부된 경우를 뜻한다.
    if (!fullText.trim()) {
      addToast({ type: 'warning', title: t('chat.questionRequired') });
      return;
    }

    const draftPayload = {
      text: fullText,
      files: [...files],
      pastedContent,
    };

    if (beforeSubmit) {
      const shouldContinue = await beforeSubmit(draftPayload);
      if (!shouldContinue) return;
    }

    const submittedFiles = cloneAttachmentsForSubmit(draftPayload.files);
    const submitPayload = {
      ...draftPayload,
      files: submittedFiles,
    };

    if (resolvedWorkspaceMode === 'agent') {
      emitAppEvent('agent:anchorLatestUser');
    }

    const shouldForceInvokeAgent =
      forceInvokeAgentOnce || pastedBlocks.some((b) => !!b.forceInvokeAgent);
    const sendOptions: { forceInvokeAgent?: boolean; modeOverride?: WorkspaceMode; reboMode?: ReboMode; activeSkillIds?: string[]; mentionedUserIds?: string[] } = {};
    if (shouldForceInvokeAgent) {
      sendOptions.forceInvokeAgent = true;
    }
    sendOptions.modeOverride = resolvedWorkspaceMode;
    sendOptions.reboMode = reboMode;
    if (reboMode === 'skill' && activeSkillIds.length > 0) {
      sendOptions.activeSkillIds = activeSkillIds;
    }

    // 그룹채팅 팀원 @멘션: 칩의 member:{user_id} 토큰을 수집해 전달.
    // 저장되는 content는 @표시이름 평문이라 user_id는 여기서만 알 수 있다.
    if (isGroupChat && editorRef.current) {
      const mentionedUserIds = Array.from(
        editorRef.current.querySelectorAll<HTMLElement>('[data-mention-chip="true"]'),
      )
        .filter((el) => el.dataset.mentionKind === 'member')
        .map((el) => (el.dataset.mentionId || '').replace(/^member:/, '').trim())
        .filter(Boolean);
      if (mentionedUserIds.length > 0) {
        sendOptions.mentionedUserIds = [...new Set(mentionedUserIds)];
      }
    }

    // 스킬 모드는 1회성: 요청을 보내고 나면 자동으로 Auto 모드로 복귀시켜
    // 다음 요청이 의도치 않게 스킬 모드로 전송되는 것을 방지한다.
    // (활성 스킬 선택은 유지하되, reboMode === 'skill' 일 때만 실제로 적용됨)
    if (reboMode === 'skill') {
      setReboMode('auto');
    }

    resetComposer();

    if (onSubmitOverride) {
      await onSubmitOverride(submitPayload);
    } else if (resolvedWorkspaceMode === 'live-agent') {
      const { sendTextFn } = (await import('@auw/stores')).useLiveAgentStore.getState();
      if (sendTextFn && fullText.trim()) {
        sendTextFn(fullText.trim());
      }
    } else {
      sendMessage(
        fullText,
        submittedFiles.length > 0 ? submittedFiles : undefined,
        Object.keys(sendOptions).length > 0 ? sendOptions : undefined,
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    /* 한글 등 IME 조합 중에는 Enter 무시 (마지막 글자 중복 방지) */
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;

    if (mentionMenu.open && filteredMentionSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionMenu((prev) => ({
          ...prev,
          activeIndex: (prev.activeIndex + 1) % filteredMentionSuggestions.length,
        }));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionMenu((prev) => ({
          ...prev,
          activeIndex:
            (prev.activeIndex - 1 + filteredMentionSuggestions.length)
            % filteredMentionSuggestions.length,
        }));
        return;
      }
      if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault();
        const picked = filteredMentionSuggestions[mentionMenu.activeIndex];
        if (picked) insertMentionChip(picked);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMentionMenu();
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      if (isBusy) return;
      e.preventDefault();
      void handleSubmit();
    }
  };

  const canSend = editorPlainText.trim().length > 0 || files.length > 0 || pastedBlocks.length > 0;
  const isBusy = isStreaming || isUploadingConversation;
  const safeSurfaceOpacity = Math.min(1, Math.max(0.35, surfaceOpacity));

  const handlePlusClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPlusMenu({ open: true, x: rect.left, y: rect.top - 4 });
  };

  /* Phase 11-5: tenant sub-feature gates for each attach source.
   * Each branch of the `+` menu maps 1:1 to a flag in the
   * `mode.agent.*` family declared in `@auw/feature-flags`.  Disabling
   * a source (e.g. on-prem tenants that ban external-cloud egress) drops
   * the entire sub-menu so users cannot even see the provider list.
   *
   * The B2C `b2c.manifest.ts` enables every flag by default — this
   * is zero-cost when all sources are on.
   *
   * PR-FE-1: switched from ``useFeatureFlags().isEnabled('mode.agent.X')``
   * to :func:`useChatFeature` so the namespace follows the active
   * :data:`ChatModeContext` (``mode.<active>.X``). For unified-agent
   * mounts (default ``mode='agent'``) the resolved ids are identical
   * to the pre-PR-FE-1 hard-coded ones. */
  const tenant = useTenant();
  /* "+ → 파일 저장소" 서브메뉴에 노출할 테넌트 등록 전문 에이전트.
   * 호스트가 storage Tree/List 를 등록(`setSpecializedStorageComponents`)한
   * 기능만 노출해, 클릭 시 항상 파일 리스트 모달이 동작하도록 한다.
   * (레지스트리는 bootstrap 시점에 채워지므로 specializedAgents 변화에만
   * 반응하는 useMemo 로 충분하다.) */
  const workspaceFileSources = useMemo(
    () =>
      resolveSpecializedAgents(tenant).filter(
        (agent) => getSpecializedStorageComponents(agent.id) !== null,
      ),
    [tenant],
  );
  /* "+ → 파일 저장소" 서브메뉴에 노출할 코어 워크스페이스 모드(통합/라이브/
   * 문서). 테넌트가 해당 모드를 활성화(`mode.<id>`)한 경우에만 노출하며,
   * 사이드바와 동일하게 테넌트 modeOrder 를 따른다. 전문 에이전트(CAD/견적)
   * 와 달리 파일은 공통 workspace_files 에 저장되므로 모달이 모드별 flat
   * 리스트로 보여준다. */
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();
  const coreFileSources = useMemo(
    () =>
      resolveTenantModeOrder(tenant, WORKSPACE_ORDER, isWorkspaceMode).filter(
        (mode): mode is 'agent' | 'live-agent' | 'doc-agent' =>
          (['agent', 'live-agent', 'doc-agent'] as readonly WorkspaceMode[]).includes(
            mode,
          ) && isFeatureEnabled(`mode.${mode}`),
      ),
    [tenant, isFeatureEnabled],
  );
  const cloudAttachEnabled = useChatFeature('cloudAttach');
  const mailAttachEnabled = useChatFeature('mailAttach');
  const collabAttachEnabled = useChatFeature('collabAttach');
  const messengerAttachEnabled = useChatFeature('messengerAttach');
  const skillUploadEnabled = useChatFeature('skillUpload');
  const skillBrowseEnabled = useChatFeature('skillBrowse');
  const skillLoadEnabled = skillUploadEnabled || skillBrowseEnabled;
  /* P11-5e: `groupChatMentionEnabled` is declared near the top of the
   * component to satisfy TDZ ordering with `updateMentionMenuFromSelection`. */

  /* Phase 12 — tenant assets:
   * Each `+` dropdown row may opt into a tenant-supplied vendor logo
   * via `tenant.plugins.overrides[<id>].meta.logoSrc`. We deliberately
   * branch on tenant overrides ONLY (not the builtin descriptor logo)
   * so B2C and any tenant that hasn't customised the row keeps the
   * existing lucide-icon look — only opted-in tenants light up the
   * branded image. The `<id>` matches `BUILTIN_PLUGIN_IDS`
   * (`google_drive`, `onedrive`, `gmail`, `outlook`, `atlassian`,
   * `notion`, `slack`, `microsoft_teams`, `discord`). */
  const renderVendorIcon = (
    vendorId: string,
    fallback: React.ReactNode,
    alt: string,
  ): React.ReactNode => {
    const overrideSrc = tenant.plugins?.overrides?.[vendorId]?.meta?.logoSrc;
    if (overrideSrc) {
      return (
        <img
          src={overrideSrc}
          alt={alt}
          style={{ width: 15, height: 15 }}
        />
      );
    }
    return fallback;
  };

  const plusMenuItems: DropdownMenuItem[] = [
    {
      id: 'local-upload',
      label: t('chat.localUpload'),
      icon: <Upload size={15} />,
      onClick: () => fileRef.current?.click(),
    },
    ...(cloudAttachEnabled
      ? [
          {
            id: 'cloud-upload',
            label: t('chat.cloudUpload'),
            icon: <Cloud size={15} />,
            onClick: () => {},
            children: [
              {
                id: 'google-drive',
                label: 'Google Drive',
                icon: renderVendorIcon(
                  'google_drive',
                  <img
                    src="/logo/google_drive.png"
                    alt="Google Drive"
                    style={{ width: 15, height: 15 }}
                  />,
                  'Google Drive',
                ),
                onClick: () => {
                  setCloudDriveModal({ open: true, initialTab: 'google_drive' });
                },
              },
              {
                id: 'onedrive',
                label: 'OneDrive',
                icon: renderVendorIcon(
                  'onedrive',
                  <img
                    src="/logo/onedrive.png"
                    alt="OneDrive"
                    style={{ width: 15, height: 15, objectFit: 'contain' }}
                  />,
                  'OneDrive',
                ),
                onClick: () => {
                  setCloudDriveModal({ open: true, initialTab: 'onedrive' });
                },
              },
            ],
          } satisfies DropdownMenuItem,
        ]
      : []),
    ...(mailAttachEnabled
      ? [
          {
            id: 'mail',
            label: t('chat.mail'),
            icon: <Mail size={15} />,
            onClick: () => {},
            children: [
              {
                id: 'gmail',
                label: 'Gmail',
                icon: renderVendorIcon(
                  'gmail',
                  <img
                    src="/logo/google_gmail.webp"
                    alt="Gmail"
                    style={{ width: 15, height: 15 }}
                  />,
                  'Gmail',
                ),
                onClick: () => {
                  setEmailModal({ open: true, initialTab: 'gmail' });
                },
              },
              {
                id: 'outlook-mail',
                label: 'Outlook',
                icon: renderVendorIcon(
                  'outlook',
                  <img
                    src="/logo/outlook.webp"
                    alt="Outlook"
                    style={{ width: 15, height: 15 }}
                  />,
                  'Outlook',
                ),
                onClick: () => {
                  setEmailModal({ open: true, initialTab: 'outlook' });
                },
              },
            ],
          } satisfies DropdownMenuItem,
        ]
      : []),
    ...(collabAttachEnabled
      ? [
          {
            id: 'collaboration-tools',
            label: t('chat.collaboration'),
            icon: <Blocks size={15} />,
            onClick: () => {},
            children: [
              {
                id: 'confluence',
                label: 'Confluence',
                icon: renderVendorIcon(
                  'atlassian',
                  <img
                    src="/logo/atlassian.png"
                    alt="Confluence"
                    style={{ width: 15, height: 15 }}
                  />,
                  'Confluence',
                ),
                onClick: () => {
                  setCollaborationModal({ open: true, initialTab: 'confluence' });
                },
              },
              {
                id: 'notion',
                label: 'Notion',
                icon: renderVendorIcon(
                  'notion',
                  <img
                    src="/logo/notion.png"
                    alt="Notion"
                    style={{ width: 15, height: 15 }}
                  />,
                  'Notion',
                ),
                onClick: () => {
                  setCollaborationModal({ open: true, initialTab: 'notion' });
                },
              },
            ],
          } satisfies DropdownMenuItem,
        ]
      : []),
    ...(messengerAttachEnabled
      ? [
          {
            id: 'messenger-upload',
            label: t('chat.messengerUpload'),
            icon: <MessageCircle size={15} />,
            onClick: () => {},
            children: [
              {
                id: 'slack-messenger',
                label: 'Slack',
                icon: renderVendorIcon(
                  'slack',
                  <img
                    src="/logo/slack.png"
                    alt="Slack"
                    style={{ width: 15, height: 15 }}
                  />,
                  'Slack',
                ),
                onClick: () => {
                  setMessengerModal({ open: true, initialTab: 'slack' });
                },
              },
              {
                id: 'microsoft-teams-messenger',
                label: 'Microsoft Teams',
                icon: renderVendorIcon(
                  'microsoft_teams',
                  <img
                    src="/logo/teams.webp"
                    alt="Microsoft Teams"
                    style={{ width: 15, height: 15 }}
                  />,
                  'Microsoft Teams',
                ),
                onClick: () => {
                  setMessengerModal({ open: true, initialTab: 'microsoft_teams' });
                },
              },
              {
                id: 'discord-messenger',
                label: 'Discord',
                icon: renderVendorIcon(
                  'discord',
                  <img
                    src="/logo/discord.png"
                    alt="Discord"
                    style={{ width: 15, height: 15 }}
                  />,
                  'Discord',
                ),
                onClick: () => {
                  setMessengerModal({ open: true, initialTab: 'discord' });
                },
              },
            ],
          } satisfies DropdownMenuItem,
        ]
      : []),
    ...(skillLoadEnabled
      ? [
          {
            id: 'skill-load',
            label: t('chat.skillLoad'),
            icon: <Sparkles size={15} />,
            onClick: () => {},
            children: [
              ...(skillUploadEnabled
                ? [
                    {
                      id: 'skill-upload-my',
                      label: t('chat.skillUploadMy'),
                      icon: <Upload size={15} />,
                      onClick: () => {
                        openSkillModal('my');
                      },
                    } satisfies DropdownMenuItem,
                  ]
                : []),
              ...(skillBrowseEnabled
                ? [
                    {
                      id: 'skill-browse',
                      label: t('chat.skillBrowse'),
                      icon: <BookOpen size={15} />,
                      onClick: () => {
                        openSkillModal('ref');
                      },
                    } satisfies DropdownMenuItem,
                  ]
                : []),
            ],
          } satisfies DropdownMenuItem,
        ]
      : []),
    /* 파일 저장소 (공통) — 테넌트에 등록된 전문 에이전트 워크스페이스를
     * 서브메뉴로 나열하고, 클릭하면 그 워크스페이스의 파일 리스트를
     * 계층 폴더 구조로 모달에 띄운다. */
    ...(workspaceFileSources.length > 0 || coreFileSources.length > 0
      ? [
          {
            id: 'workspace-files',
            label: t('chat.workspaceFiles', { defaultValue: '파일 저장소' }),
            icon: <HardDrive size={15} />,
            onClick: () => {},
            children: [
              /* 코어 모드(통합/라이브/문서) — workspace_files flat 리스트. */
              ...coreFileSources.map((mode) => {
                const label = t(WORKSPACE_MODES[mode].labelKey);
                return {
                  id: `workspace-files:mode:${mode}`,
                  label,
                  icon: <FolderOpen size={15} />,
                  onClick: () => {
                    setWorkspaceFilesModal({
                      open: true,
                      mode,
                      agentLabel: label,
                    });
                  },
                } satisfies DropdownMenuItem;
              }),
              /* 전문 에이전트(CAD/견적) — 도메인 폴더 트리. */
              ...workspaceFileSources.map((agent) => {
                const label = agent.labelKey ? t(agent.labelKey) : agent.label;
                return {
                  id: `workspace-files:${agent.id}`,
                  label,
                  icon: <FolderOpen size={15} />,
                  onClick: () => {
                    setWorkspaceFilesModal({
                      open: true,
                      agentId: agent.id,
                      agentLabel: label,
                    });
                  },
                } satisfies DropdownMenuItem;
              }),
            ],
          } satisfies DropdownMenuItem,
        ]
      : []),
  ];

  /* 견적 상세처럼 특정 항목만 노출해야 하는 surface 를 위해 top-level id
   * 화이트리스트로 필터링한다 (미지정이면 전체 노출). */
  const visiblePlusMenuItems = plusMenuItemIds
    ? plusMenuItems.filter((item) => plusMenuItemIds.includes(item.id))
    : plusMenuItems;

  useEffect(() => {
    if (!isGroupChat) {
      closeMentionMenu();
    }
  }, [closeMentionMenu, isGroupChat]);

  const handleEditorFocus = () => {
    setFocused(true);
    updateMentionMenuFromSelection();
  };

  const handleEditorBlur = () => {
    setFocused(false);
    setTimeout(() => {
      const editor = editorRef.current;
      const selection = window.getSelection();
      if (!editor || !selection || !editor.contains(selection.focusNode)) {
        closeMentionMenu();
      }
    }, 0);
  };

  const handleEditorClick = () => {
    updateMentionMenuFromSelection();
  };

  return (
    <OuterWrapper ref={outerRef} $surfaceOpacity={safeSurfaceOpacity}>
      <Wrapper>
        <InputCard $focus={focused}>
          {/* Upload progress */}
          {isUploadingConversation && (
            <UploadingBadge>
              <Loader size={14} />
              {t('chat.fileUploading')}
            </UploadingBadge>
          )}

          {/* Attachments */}
          {(files.length > 0 || pastedBlocks.length > 0) && (
            <Attachments>
              {/* Pasted text blocks */}
              {pastedBlocks.map((block) => (
                <PastedCard key={block.id} onClick={() => setViewingBlock(block)}>
                  <PastedPreview>{block.content}</PastedPreview>
                  <PastedFooter>
                    <PastedBadge>PASTED</PastedBadge>
                    <PastedCharCount>{block.content.length.toLocaleString()}{t('chat.charCount')}</PastedCharCount>
                  </PastedFooter>
                  <PastedRemoveBtn
                    onClick={(e) => { e.stopPropagation(); removePastedBlock(block.id); }}
                  >
                    <X size={10} />
                  </PastedRemoveBtn>
                </PastedCard>
              ))}

              {files.map((f) => {
                const isImage = f.type.startsWith('image/');
                const info = getFileTypeInfo(f.name, f.type);
                const previewable = canPreviewFile(f.name);
                return (
                  <FileCard key={f.id} $previewable={previewable}>
                    {isImage && f.previewUrl ? (
                      <ImageThumb src={f.previewUrl} alt={f.name} />
                    ) : (
                      <FileIconBadge $bg={info.bg} $color={info.color}>
                        {info.icon}
                        <FileExtLabel $color={info.color}>{info.ext}</FileExtLabel>
                      </FileIconBadge>
                    )}
                    <FileInfo>
                      <FileName title={f.name}>{f.name}</FileName>
                      <FileSize>{formatFileSize(f.size)}</FileSize>
                    </FileInfo>
                    <CardActions>
                      {previewable && (() => {
                        // 렌더링이 실제로 보이는 상태:
                        // wide → previewFile이 이 파일로 설정된 경우
                        // narrow → previewFile이 설정됐고 preview 뷰인 경우
                        const isRendering =
                          previewFile?.id === f.id &&
                          (!isNarrowLayout || narrowPreviewView === 'preview');
                        return isRendering ? (
                          <Tooltip label={t('tooltip.collapse')}>
                            <CardActionBtn
                              onClick={() =>
                                isNarrowLayout
                                  ? setNarrowPreviewView('chat')
                                  : setPreviewFile(null)
                              }
                            >
                              <Minimize2 size={12} />
                            </CardActionBtn>
                          </Tooltip>
                        ) : (
                          <Tooltip label={t('tooltip.expand')}>
                            <CardActionBtn onClick={() => setPreviewFile(f)}>
                              <Maximize2 size={12} />
                            </CardActionBtn>
                          </Tooltip>
                        );
                      })()}
                      <Tooltip label={t('button.delete')}>
                      <CardActionBtn onClick={() => removeFile(f.id)}>
                        <X size={12} />
                      </CardActionBtn>
                      </Tooltip>
                    </CardActions>
                  </FileCard>
                );
              })}
            </Attachments>
          )}

          <HiddenInput
            ref={fileRef}
            type="file"
            multiple
            onChange={(e) => {
              addFiles(e.target.files);
              e.currentTarget.value = '';
            }}
          />

          {/* 텍스트 입력 (그룹채팅 멘션 자동완성/칩 지원) */}
          <ComposerInputWrap>
            <EditorInput
              ref={editorRef}
              contentEditable={!isUploadingConversation}
              suppressContentEditableWarning
              role="textbox"
              aria-multiline="true"
              data-empty={editorIsEmpty ? 'true' : 'false'}
              data-at-max={editorAtMax ? 'true' : 'false'}
              onInput={handleEditorInput}
              onFocus={handleEditorFocus}
              onBlur={handleEditorBlur}
              onClick={handleEditorClick}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
            />
            {editorIsEmpty && !focused && (
              <ComposerPlaceholder>{composerPlaceholder}</ComposerPlaceholder>
            )}
          </ComposerInputWrap>

          <InputRow>
            <InputRowLeft>
              {/* + 메뉴 */}
              <Tooltip label={t('tooltip.menu')} position="top">
                <IconBtn onClick={handlePlusClick} disabled={isBusy}>
                  <Plus size={18} />
                </IconBtn>
              </Tooltip>

              {/* Rebo 모드 선택기 (live-agent 모드에서는 Fast 고정, 드롭다운 숨김) */}
              {resolvedWorkspaceMode === 'live-agent' ? (
                <ReboModeArea>
                  <ReboModeLabel style={{ padding: '4px 8px', opacity: 0.7 }}>
                    {t('reboMode.fastLabel')}
                  </ReboModeLabel>
                </ReboModeArea>
              ) : (
                <ReboModeArea>
                  <ReboModeTrigger
                    ref={reboTriggerRef}
                    type="button"
                    onClick={() => setReboMenuOpen((v) => !v)}
                    $open={reboMenuOpen}
                  >
                    <ReboModeLabel>{reboModeOptions.find((o) => o.id === reboMode)?.label ?? 'Auto'}</ReboModeLabel>
                    <ChevronDown size={12} />
                  </ReboModeTrigger>

                  {reboMenuOpen && (
                    <ReboModePopup ref={reboMenuRef}>
                      <ReboModePopupHeader>Rebo</ReboModePopupHeader>
                      {reboModeOptions.map((opt) => (
                        <ReboModeRow
                          key={opt.id}
                          $active={reboMode === opt.id}
                          onClick={() => {
                            setReboMode(opt.id);
                            setReboMenuOpen(false);
                            if (opt.id === 'skill') openSkillModal();
                          }}
                        >
                          <ReboModeRowInfo>
                            <ReboModeRowName>{opt.label}</ReboModeRowName>
                            <ReboModeRowDesc>{opt.description}</ReboModeRowDesc>
                          </ReboModeRowInfo>
                          {reboMode === opt.id && (
                            <ReboModeCheck><Check size={16} /></ReboModeCheck>
                          )}
                        </ReboModeRow>
                      ))}
                    </ReboModePopup>
                  )}
                </ReboModeArea>
              )}

              {/* Skill 모드 활성 스킬 Chips */}
              {reboMode === 'skill' && activeSkillIds.length > 0 && (
                <SkillChipArea>
                  {activeSkillIds.map((sid) => {
                    const sk = skillsList.find((s) => s.skill_id === sid);
                    const offSk = !sk ? officialSkills.find((o) => o.skill_id === sid) : null;
                    const label = sk?.name
                      ?? (offSk ? resolveLocalized(offSk.display_name, i18n.language, offSk.name) : null)
                      ?? sid;
                    return (
                      <Chip
                        key={sid}
                        size="sm"
                        variant="filled"
                        active
                        clickable
                        onClick={() => { toggleSkill(sid); setReboMode('auto'); }}
                      >
                        {label}
                        <X size={10} style={{ marginLeft: 2 }} />
                      </Chip>
                    );
                  })}
                </SkillChipArea>
              )}
            </InputRowLeft>

            {/* 전송 / 중지 / 업로드 중 */}
            {isUploadingConversation ? (
              <Tooltip label={t('chat.fileUploading')}>
                <IconBtn $primary $disabled>
                  <SpinIcon><Loader size={18} /></SpinIcon>
                </IconBtn>
              </Tooltip>
            ) : isStreaming ? (
              <Tooltip label={t('tooltip.stopResponse')}>
                <IconBtn $primary onClick={() => stopStreaming(runtimeConversationId)}>
                  <Square size={16} fill="currentColor" />
                </IconBtn>
              </Tooltip>
            ) : (
              <Tooltip label={t('tooltip.send')}>
                <IconBtn $primary $disabled={!canSend} onClick={handleSubmit} disabled={!canSend}>
                  <ArrowUp size={18} />
                </IconBtn>
              </Tooltip>
            )}
          </InputRow>
        </InputCard>

      </Wrapper>

      {mentionMenu.open && filteredMentionSuggestions.length > 0 && (
        <MentionDropdown style={{ left: mentionMenu.x, top: mentionMenu.y }}>
          {filteredMentionSuggestions.map((mention, idx) => (
            <MentionSuggestionRow
              key={mention.id}
              $focused={idx === mentionMenu.activeIndex}
              onMouseEnter={() => {
                setMentionMenu((prev) => ({ ...prev, activeIndex: idx }));
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                insertMentionChip(mention);
              }}
            >
              <MentionSuggestionType>
                {mention.kind === 'agent' ? 'Agent' : t('chat.teamMember')}
              </MentionSuggestionType>
              <MentionSuggestionLabel>{mention.display}</MentionSuggestionLabel>
            </MentionSuggestionRow>
          ))}
        </MentionDropdown>
      )}

      {/* + 드롭다운 메뉴 */}
      <DropdownMenu
        open={plusMenu.open}
        x={plusMenu.x}
        y={plusMenu.y}
        direction="up"
        align="left"
        onClose={() => setPlusMenu((prev) => ({ ...prev, open: false }))}
        items={visiblePlusMenuItems}
      />

      {/* 파일 저장소 브라우저 모달 (+ → 파일 저장소 → <기능>) */}
      <WorkspaceFilesModal
        open={workspaceFilesModal.open}
        onClose={() =>
          setWorkspaceFilesModal((prev) => ({ ...prev, open: false }))
        }
        agentId={workspaceFilesModal.agentId}
        mode={workspaceFilesModal.mode}
        agentLabel={workspaceFilesModal.agentLabel}
        onAttach={appendAttachmentEntries}
      />

      {/* 클라우드 드라이브 모달 */}
      {cloudDriveModal.open && (
        <Suspense fallback={null}>
          <CloudDriveModal
            open
            onClose={() => setCloudDriveModal((prev) => ({ ...prev, open: false }))}
            initialTab={cloudDriveModal.initialTab}
            existingAttachmentCount={files.length}
            onSelect={(cloudFiles: FileAttachment[]) => appendAttachmentEntries(cloudFiles)}
            modalCenterPaddingLeft={modalCenterPadding.left}
            modalCenterPaddingRight={modalCenterPadding.right}
          />
        </Suspense>
      )}

      {/* 이메일 모달 */}
      {emailModal.open && (
        <Suspense fallback={null}>
          <EmailModal
            open
            onClose={() => setEmailModal((prev) => ({ ...prev, open: false }))}
            initialTab={emailModal.initialTab}
            existingAttachmentCount={files.length}
            onSelect={(mailFiles: FileAttachment[]) => appendAttachmentEntries(mailFiles)}
            modalCenterPaddingLeft={modalCenterPadding.left}
            modalCenterPaddingRight={modalCenterPadding.right}
          />
        </Suspense>
      )}

      {/* 협업툴 모달 */}
      {collaborationModal.open && (
        <Suspense fallback={null}>
          <CollaborationModal
            open
            onClose={() => setCollaborationModal((prev) => ({ ...prev, open: false }))}
            initialTab={collaborationModal.initialTab}
            existingAttachmentCount={files.length}
            onSelect={(collabFiles: FileAttachment[]) => appendAttachmentEntries(collabFiles)}
            modalCenterPaddingLeft={modalCenterPadding.left}
            modalCenterPaddingRight={modalCenterPadding.right}
          />
        </Suspense>
      )}

      {/* 메신저 업로드 모달 */}
      {messengerModal.open && (
        <Suspense fallback={null}>
          <MessengerModal
            open
            onClose={() => setMessengerModal((prev) => ({ ...prev, open: false }))}
            initialTab={messengerModal.initialTab}
            existingAttachmentCount={files.length}
            onSelect={(messengerFiles: FileAttachment[]) => appendAttachmentEntries(messengerFiles)}
            modalCenterPaddingLeft={modalCenterPadding.left}
            modalCenterPaddingRight={modalCenterPadding.right}
          />
        </Suspense>
      )}

      {/* 붙여넣은 텍스트 뷰어 모달 */}
      <Modal
        open={!!viewingBlock}
        onClose={() => setViewingBlock(null)}
        title={t('chat.pastedContent')}
        titleIcon={<Clipboard size={16} />}
        width="580px"
      >
        {viewingBlock && (
          <PastedModalContent>
            <PastedModalTextarea
              value={viewingBlock.content}
              onChange={(e) => {
                const next = { ...viewingBlock, content: e.target.value };
                setViewingBlock(next);
                setPastedBlocks((prev) =>
                  prev.map((b) => (b.id === viewingBlock.id ? next : b)),
                );
              }}
              autoFocus
            />
            <PastedModalFooter>
              <PastedModalConfirmBtn onClick={() => setViewingBlock(null)}>
                {t('button.confirm')}
              </PastedModalConfirmBtn>
            </PastedModalFooter>
          </PastedModalContent>
        )}
      </Modal>

      {/* 스킬 관리 모달 */}
      <SkillModal
        open={skillModalOpen}
        onClose={closeSkillModal}
        initialTab={skillModalInitialTab}
        centerPaddingLeft={modalCenterPadding.left}
        centerPaddingRight={modalCenterPadding.right}
      />

    </OuterWrapper>
  );
};

/* ---- Skill Chip Area ---- */

const SkillChipArea = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  margin-left: 4px;
`;

/* ---- Rebo Mode Selector ---- */

const InputRowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ReboModeArea = styled.div`
  position: relative;
`;

const ReboModeTrigger = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 2px;
  border-radius: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: color ${({ theme }) => theme.transitions.fast};
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  ${({ $open, theme }) =>
    $open &&
    css`
      color: ${theme.colors.textPrimary};
    `}

  svg {
    transition: transform 0.15s ease;
    ${({ $open }) => $open && css`transform: rotate(180deg);`}
  }
`;

const ReboModeLabel = styled.span`
  line-height: 1;
`;

const ReboModePopup = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  min-width: 220px;
  padding: 6px;
  border-radius: 14px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(40, 40, 42, 0.96)'
      : 'rgba(255, 255, 255, 0.96)'};
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.3)'
      : '0 8px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)'};
  z-index: 10;
  animation: ${fadeIn} 0.15s ease;
`;

const ReboModePopupHeader = styled.div`
  padding: 6px 10px 4px;
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  letter-spacing: 0.02em;
`;

const ReboModeRow = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
  padding: 8px 10px;
  border: none;
  border-radius: 10px;
  background: ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.05)'
      : 'transparent'};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  text-align: left;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
  }
`;

const ReboModeRowInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ReboModeRowName = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.3;
`;

const ReboModeRowDesc = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.3;
  margin-top: 1px;
`;

const ReboModeCheck = styled.div`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.accent};
  display: flex;
  align-items: center;
`;

